'use client';

import { useState } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useRouter } from 'next/navigation';
import AppLogo from '@/components/ui/AppLogo';

export default function LoginPage() {
  const { signIn } = useAuth();
  const router = useRouter();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      await signIn(email, password);
      router.push('/company-dashboard');
    } catch (err: any) {
      setError(err?.message || 'Credenciales incorrectas. Intenta de nuevo.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-50 dark:bg-slate-900 flex items-center justify-center p-4 overflow-x-hidden">
      <div className="w-full max-w-md min-w-0">
        {/* Card */}
        <div className="bg-white dark:bg-slate-800 rounded-2xl shadow-sm border border-slate-200 dark:border-slate-700 p-5 sm:p-8 overflow-hidden">
          {/* Logo */}
          <div className="flex items-center gap-3 mb-8 min-w-0">
            <AppLogo size={40} />
            <div className="min-w-0">
              <div className="flex items-baseline gap-0.5 flex-wrap">
                <span className="font-bold text-slate-800 dark:text-slate-100 text-xl tracking-tight">CG</span>
                <span className="font-bold text-orange-500 text-xl tracking-tight">Logistica</span>
              </div>
              <p className="text-xs text-slate-400 dark:text-slate-500 truncate">Plataforma de Logística Inversa</p>
            </div>
          </div>

          <h1 className="text-lg font-semibold text-slate-800 dark:text-slate-100 mb-1">Iniciar sesión</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400 mb-6">Ingresa tus credenciales para continuar</p>

          {/* Form */}
          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Correo electrónico
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="usuario@cglogistica.com"
                required
                className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 transition-all"
              />
            </div>

            <div>
              <label className="block text-xs font-medium text-slate-700 dark:text-slate-300 mb-1.5">
                Contraseña
              </label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="••••••••"
                required
                className="w-full px-3 py-2.5 text-sm border border-slate-200 dark:border-slate-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-transparent bg-white dark:bg-slate-700 text-slate-800 dark:text-slate-100 placeholder-slate-400 dark:placeholder-slate-500 transition-all"
              />
            </div>

            {error && (
              <div className="flex items-center gap-2 p-3 bg-red-50 dark:bg-red-500/10 border border-red-100 dark:border-red-500/20 rounded-lg">
                <svg className="w-4 h-4 text-red-500 flex-shrink-0" fill="currentColor" viewBox="0 0 20 20">
                  <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                </svg>
                <p className="text-xs text-red-600 dark:text-red-400 break-words min-w-0">{error}</p>
              </div>
            )}

            <button
              type="submit"
              disabled={loading}
              className="w-full bg-orange-500 hover:bg-orange-600 disabled:bg-orange-300 text-white font-semibold py-2.5 px-4 rounded-lg text-sm transition-colors duration-150 flex items-center justify-center gap-2"
            >
              {loading ? (
                <>
                  <svg className="animate-spin w-4 h-4 flex-shrink-0" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z" />
                  </svg>
                  Iniciando sesión...
                </>
              ) : (
                'Iniciar sesión'
              )}
            </button>
          </form>

          {/* Test credentials info */}
          <div className="mt-5 p-3 bg-slate-50 dark:bg-slate-700/50 rounded-lg border border-slate-100 dark:border-slate-600 overflow-hidden">
            <p className="text-xs font-medium text-slate-600 dark:text-slate-300 mb-2">Usuarios de prueba — CG Logistica:</p>
            <div className="space-y-1">
              <p className="text-xs text-slate-500 dark:text-slate-400 break-all"><span className="font-medium">Super Admin:</span> admin@cglogistica.com / Admin123!</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 break-all"><span className="font-medium">Operador:</span> test.operator@cglogistica.com / Test1234!</p>
              <p className="text-xs text-slate-500 dark:text-slate-400 break-all"><span className="font-medium">Viewer:</span> test.viewer@cglogistica.com / Test1234!</p>
            </div>
          </div>

          <div className="mt-4 pt-4 border-t border-slate-100 dark:border-slate-700 text-center">
            <p className="text-xs text-slate-400 dark:text-slate-500 break-words">
              ¿Problemas para acceder? Contacta al administrador del sistema.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
}
